import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import * as T from '../types';
import { money, parseNumber } from '../labels';

/**
 * A one-off purchase (decoration, a utensil): what it is and its price. It counts in the trip total, but is not
 * added to the catalog, the stock or the price history.
 */
const ExtraDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (extra: T.TripExtraRequest) => Promise<string | null>;
}> = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [amount, setAmount] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDescription('');
    setPrice('');
    setAmount('1');
    setError(null);
  }, [open]);

  const unitPrice = parseNumber(price);
  const count = amount.trim() ? parseNumber(amount) : 1;
  const total = unitPrice !== null && count !== null ? Math.round(unitPrice * count * 100) / 100 : null;

  const submit = async () => {
    if (!description.trim()) return setError('Indica o que é.');
    if (unitPrice === null || unitPrice < 0) return setError('Preço inválido.');
    if (count === null || count <= 0) return setError('Quantidade inválida.');
    setBusy(true);
    setError(null);
    try {
      const note = await onSubmit({ description: description.trim(), quantity: count, unitPrice });
      if (note) setError(note);
      else onClose();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="xs">
      <DialogTitle>Avulso</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Conta para o total da ida, mas não fica no catálogo, no stock nem no histórico de preços.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="O que é"
            placeholder="Ex.: Jarra, frigideira"
            value={description}
            onChange={e => setDescription(e.target.value)}
            inputProps={{ maxLength: 80 }}
            autoFocus
          />
          <Stack direction="row" spacing={1}>
            <TextField
              label="Preço de cada (€)"
              value={price}
              onChange={e => setPrice(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              sx={{ flexGrow: 1 }}
            />
            <TextField
              label="Quantos"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              sx={{ width: 100 }}
            />
          </Stack>
          {total !== null && <Typography variant="body2">Total: <b>{money(total)}</b></Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>Pôr no carrinho</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtraDialog;
